/**
 * Visitor-scoped storage management — Trilho I (RF-022, CS-011).
 *
 * Provides a sweep of `fiinder_*` keys (except company-scoped whitelist)
 * across both `localStorage` and `sessionStorage`, plus a marker-based
 * detection of visitor changes across page reloads. See
 * `src/components/api/TEST_DOC.md` for the test matrix that drives this
 * module.
 */

const ACTIVE_VISITOR_KEY = "fiinder_active_visitor";

/**
 * Keys inside the `fiinder_*` namespace that are scoped to the company
 * (not to the individual visitor) — these MUST be preserved across
 * visitor changes. Adding new visitor-scoped storage requires nothing
 * here; adding new company-scoped storage requires registering its key
 * in this set.
 */
const COMPANY_SCOPED_KEYS = new Set<string>([
  "fiinder_cached_user_id",
  ACTIVE_VISITOR_KEY,
]);

/**
 * Removes every `fiinder_*` key from `localStorage` and `sessionStorage`
 * except those listed in `COMPANY_SCOPED_KEYS`. The TanStack Query
 * persister's key is intentionally outside the `fiinder_*` namespace
 * managed here — its lifecycle is owned by the TanStack library and
 * accessed exclusively through its dedicated API
 * (`queryClient.clear()`, `persister.removeClient()`).
 */
export function clearVisitorScopedFiinderStorage(): void {
  for (const storage of [window.localStorage, window.sessionStorage]) {
    const keysToRemove = Object.keys(storage).filter(
      (key) => key.startsWith("fiinder_") && !COMPANY_SCOPED_KEYS.has(key),
    );
    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }
}

/**
 * Compares `currentVisitor` against the value stored in
 * `localStorage[fiinder_active_visitor]`. When different, sweeps the
 * visitor-scoped storage (via `clearVisitorScopedFiinderStorage`) and
 * returns `true` so callers can decide whether to invoke library-owned
 * cleanups (e.g. `persister.removeClient()`).
 *
 * Always updates the stored marker to the current value before
 * returning, so subsequent calls compare against the new identity.
 *
 * First boot (marker missing) is treated as "no change" — there is
 * no previous identity to leak from. Falsy `undefined` is coalesced
 * to the empty string so transitions `alice → undefined → alice`
 * do not appear as changes.
 */
export function rotateStorageIfVisitorChanged(
  currentVisitor: string | undefined,
): boolean {
  const previous = window.localStorage.getItem(ACTIVE_VISITOR_KEY);
  const current = currentVisitor ?? "";
  const changed = previous !== null && previous !== current;
  if (changed) {
    clearVisitorScopedFiinderStorage();
  }
  window.localStorage.setItem(ACTIVE_VISITOR_KEY, current);
  return changed;
}
